/* AIAgentWidget.jsx — Claude Sonnet 4.5 powered analysis & request suggestions */
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Brain, Sparkles, AlertTriangle, TrendingUp, ClipboardList, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

export function AIAgentWidget({ barId }) {
    const [analysis, setAnalysis] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [expectedGuests, setExpectedGuests] = useState('');

    const runAnalysis = async () => {
        if (!barId) {
            toast.error('Önce bir bar seçin');
            return;
        }
        setLoading(true);
        setAnalysis(null);
        try {
            const body = { bar_id: barId, lookback_days: 14 };
            if (expectedGuests) body.expected_guests = parseInt(expectedGuests, 10);
            // Start background job to avoid ingress 60s timeout
            const startRes = await api.post('/agent/analyze/start', body, { timeout: 15000 });
            const jobId = startRes.data?.job_id;
            if (!jobId) throw new Error('Job ID alınamadı');

            // Poll status every 3s for up to 3 minutes
            const maxAttempts = 60;
            let attempts = 0;
            const poll = async () => {
                attempts += 1;
                const statusRes = await api.get(`/agent/analyze/status/${jobId}`, { timeout: 15000 });
                const job = statusRes.data;
                if (job.status === 'done') {
                    setAnalysis(job.result);
                    toast.success('AI analizi tamamlandı');
                    setLoading(false);
                    return;
                }
                if (job.status === 'failed') {
                    toast.error(job.error || 'AI analizi başarısız');
                    setLoading(false);
                    return;
                }
                if (attempts >= maxAttempts) {
                    toast.error('AI analizi zaman aşımına uğradı');
                    setLoading(false);
                    return;
                }
                setTimeout(poll, 3000);
            };
            setTimeout(poll, 3000);
        } catch (e) {
            toast.error(e?.response?.data?.detail || e.message || 'AI analizi başarısız');
            setLoading(false);
        }
    };

    const saveAsRequest = async () => {
        if (!analysis?.suggestions?.length) return;
        setSaving(true);
        try {
            await api.post('/agent/save-as-request', {
                bar_id: barId,
                suggestions: analysis.suggestions,
                notes: `AI Agent önerisi · ${analysis.expected_guests} misafir tahmini`,
            });
            toast.success(`${analysis.suggestions.length} ürün yarına talep olarak kaydedildi`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Kayıt başarısız');
        } finally {
            setSaving(false);
        }
    };

    if (!barId) {
        return (
            <Card data-testid="widget-ai-agent">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                        <Brain className="w-5 h-5 text-purple-600" />
                        AI Agent (Claude Sonnet 4.5)
                    </CardTitle>
                    <CardDescription>Bar seçince yapay zekâ kapsamlı analiz yapar</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="text-sm text-slate-400 p-6 text-center bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-100">
                        Yukarıdaki "Bar Bazlı Maliyet Dağılımı"ndan bir bar seçin
                    </div>
                </CardContent>
            </Card>
        );
    }

    const totalCost = (analysis?.suggestions || []).reduce((s, x) => s + (x.estimated_cost_tl || 0), 0);

    return (
        <Card data-testid="widget-ai-agent" className="border-purple-200">
            <CardHeader className="bg-gradient-to-r from-purple-50 to-blue-50">
                <div className="flex items-start justify-between flex-wrap gap-2">
                    <div>
                        <CardTitle className="flex items-center gap-2 text-lg">
                            <Brain className="w-5 h-5 text-purple-600" />
                            AI Agent
                            <Badge className="bg-purple-600 text-white">Claude Sonnet 4.5</Badge>
                        </CardTitle>
                        <CardDescription>Akıllı tüketim analizi & talep önerisi</CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                        <Input
                            type="number"
                            placeholder="Yarınki misafir"
                            value={expectedGuests}
                            onChange={(e) => setExpectedGuests(e.target.value)}
                            className="w-32 h-9"
                            data-testid="ai-expected-guests"
                        />
                        <Button
                            onClick={runAnalysis}
                            disabled={loading}
                            className="bg-purple-600 hover:bg-purple-700"
                            data-testid="ai-analyze-btn"
                        >
                            {loading ? <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
                            {loading ? 'Analiz Ediliyor...' : 'AI Analiz Et'}
                        </Button>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="pt-4">
                {loading && (
                    <div className="text-center p-8 text-slate-400">
                        <Brain className="w-12 h-12 mx-auto mb-3 text-purple-300 animate-pulse" />
                        Claude veriyi inceliyor, lütfen bekleyin (15-30 sn)...
                    </div>
                )}

                {!loading && !analysis && (
                    <div className="text-center p-6 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
                        <Brain className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                        <p className="text-sm text-slate-500">
                            "AI Analiz Et" butonuna basın — son 14 günün verisini inceleyip yarın için talep önerisi sunacak
                        </p>
                    </div>
                )}

                {!loading && analysis && (
                    <div className="space-y-4">
                        {/* Summary */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <div className="flex items-start gap-2">
                                <Sparkles className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                                <p className="text-sm text-slate-700">{analysis.summary}</p>
                            </div>
                        </div>

                        {/* Alerts */}
                        {analysis.alerts && analysis.alerts.length > 0 && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                <div className="flex items-center gap-2 mb-2">
                                    <AlertTriangle className="w-4 h-4 text-red-600" />
                                    <span className="text-sm font-semibold text-red-800">Uyarılar</span>
                                </div>
                                <ul className="text-xs space-y-1 text-slate-700">
                                    {analysis.alerts.map((a, i) => (
                                        <li key={i} className="flex items-start gap-1">
                                            <span className="text-red-500 mt-0.5">●</span>
                                            <span>{a}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Trends */}
                        {analysis.trends && analysis.trends.length > 0 && (
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                                <div className="flex items-center gap-2 mb-2">
                                    <TrendingUp className="w-4 h-4 text-amber-600" />
                                    <span className="text-sm font-semibold text-amber-800">Trend Gözlemleri</span>
                                </div>
                                <ul className="text-xs space-y-1 text-slate-700">
                                    {analysis.trends.map((t, i) => (
                                        <li key={i} className="flex items-start gap-1">
                                            <span className="text-amber-500 mt-0.5">●</span>
                                            <span>{t}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Suggestions */}
                        {analysis.suggestions && analysis.suggestions.length > 0 && (
                            <div className="border border-purple-200 rounded-lg overflow-hidden">
                                <div className="bg-purple-100 px-3 py-2 flex items-center justify-between flex-wrap gap-2">
                                    <div className="flex items-center gap-2">
                                        <ClipboardList className="w-4 h-4 text-purple-700" />
                                        <span className="text-sm font-semibold text-purple-900">
                                            Önerilen Çekiliş ({analysis.suggestions.length} ürün)
                                        </span>
                                        <Badge className="bg-white text-purple-700 border border-purple-300">
                                            {totalCost.toLocaleString('tr-TR', { maximumFractionDigits: 0 })} TL
                                        </Badge>
                                    </div>
                                    <Button
                                        onClick={saveAsRequest}
                                        disabled={saving}
                                        size="sm"
                                        className="bg-purple-600 hover:bg-purple-700"
                                        data-testid="ai-save-request-btn"
                                    >
                                        {saving ? 'Kaydediliyor...' : 'Yarına Talep Olarak Kaydet'}
                                    </Button>
                                </div>
                                <div className="divide-y max-h-80 overflow-auto">
                                    {analysis.suggestions.map((s, idx) => (
                                        <div key={idx} className="p-2.5 flex items-start justify-between hover:bg-purple-50" data-testid={`ai-suggestion-${idx}`}>
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-medium text-sm text-slate-800">{s.product_name}</span>
                                                    <Badge variant="outline" className="text-[10px]">{s.unit}</Badge>
                                                </div>
                                                {s.reasoning && (
                                                    <p className="text-xs text-slate-500 mt-1">{s.reasoning}</p>
                                                )}
                                            </div>
                                            <div className="text-right ml-3">
                                                <div className="text-base font-bold text-purple-700">{s.suggested_quantity}</div>
                                                <div className="text-[10px] text-slate-400">{(s.estimated_cost_tl || 0).toLocaleString('tr-TR')} TL</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {analysis.suggestions?.length === 0 && (
                            <div className="text-center p-4 bg-emerald-50 rounded text-sm text-emerald-700">
                                ✓ Stok yeterli — yarın için ek talep önerilmiyor
                            </div>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

export default AIAgentWidget;
